import React, { useEffect, useRef, useState, useCallback } from 'react';
import Layout, { type AppView } from './components/Layout';
import Settings from './components/Settings';
import LoginModal from './components/LoginModal';
import OrgSelector from './components/OrgSelector';
import UploadZone from './components/UploadZone';
import AutomatedXeroPanel from './components/AutomatedXeroPanel';
import ResultsTable from './components/ResultsTable';
import SummaryPanel from './components/SummaryPanel';
import ConfirmModal from './components/ConfirmModal';
import SuccessState from './components/SuccessState';
import QuickSuperState from './components/QuickSuperState';
import PastRuns from './components/PastRuns';
import AdminPage from './components/AdminPage';
import {
  authApi,
  accountApi,
  xeroApi,
  importApi,
  payrunApi,
  quickSuperApi,
  AUTH_UNAUTHORIZED_EVENT,
  XERO_PAYROLL_UNAUTHORIZED_EVENT,
  XERO_ORG_DISCONNECTED_EVENT,
  type XeroPayrollUnauthorizedDetail,
  type XeroOrgDisconnectedDetail,
} from './api/client';
import type {
  User,
  XeroTenant,
  XeroEmployee,
  XeroPayrollCalendar,
  TenantExpectedPeriod,
  EmployeeSuperValidation,
  ImportRecord,
  ImportLine,
  UploadResult,
  PayRunResult,
  QuickSuperMember,
  QuickSuperMatch,
  WorkflowStep,
} from './types';
import { calendarTypeLabel, employeePayrollCalendarId, employeePayrollCalendarType } from './types';

// SessionStorage key set before kicking off the InvoicePolish Xero-connect
// flow when the user clicked "Connect Xero" inside the Settings page. The
// post-callback render reads it and lands them back on Settings.
const XERO_RETURN_TO_SETTINGS_KEY = 'contractors:xero-return-to-settings';
// Xero plan limit: at most this many contractors can be sent in one pay run.
const DEFAULT_XERO_SELECTION_COUNT = 5;
const MAX_XERO_SELECTION = 5;

function norm(s: string) {
  return s.toLowerCase().replace(/[^a-z ]/g, '').trim().replace(/\s+/g, ' ');
}

function autoMatchEmployee(
  contractorName: string,
  employees: XeroEmployee[],
  inferredType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null
): XeroEmployee | null {
  const n = norm(contractorName);
  const candidates = inferredType
    ? employees.filter((employee) => employeePayrollCalendarType(employee) === inferredType)
    : employees;
  let m = candidates.find((e) => norm(`${e.FirstName} ${e.LastName}`) === n);
  if (m) return m;
  m = candidates.find((e) => norm(`${e.LastName} ${e.FirstName}`) === n);
  if (m) return m;
  m = candidates.find((e) => {
    const full = norm(`${e.FirstName} ${e.LastName}`);
    return n.includes(full) || full.includes(n);
  });
  return m ?? null;
}

function fmtReportDate(iso: string | null): string {
  if (!iso) return 'Not detected';
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function formatExpectedPeriod(period: { payPeriodStartDate?: string; payPeriodEndDate?: string }): string {
  function fmtOne(iso?: string): string {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    });
  }
  return `${fmtOne(period.payPeriodStartDate)} – ${fmtOne(period.payPeriodEndDate)}`;
}

function resolvePayrollCalendarId(
  lines: ImportLine[],
  employees: XeroEmployee[],
  calendars: XeroPayrollCalendar[],
  inferredType: 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null
): string {
  const mappedCalendarIds = Array.from(new Set(
    lines
      .map((line) => employees.find((employee) => employee.EmployeeID === line.xeroEmployeeId))
      .map(employeePayrollCalendarId)
      .filter((id): id is string => !!id)
  ));

  if (mappedCalendarIds.length === 1 && calendars.some((calendar) => calendar.PayrollCalendarID === mappedCalendarIds[0])) {
    return mappedCalendarIds[0];
  }

  if (inferredType) {
    const match = calendars.find((calendar) => calendar.CalendarType.toUpperCase() === inferredType);
    if (match) return match.PayrollCalendarID;
  }

  return calendars[0]?.PayrollCalendarID ?? '';
}

export default function App() {
  // Auth
  const [user, setUser] = useState<User | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Xero
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroTenants, setXeroTenants] = useState<XeroTenant[]>([]);
  const [employees, setEmployees] = useState<XeroEmployee[]>([]);
  const [calendars, setCalendars] = useState<XeroPayrollCalendar[]>([]);
  // Best-effort estimate of Xero's next pending pay period, inferred from
  // this tenant's most recently used payroll calendar. Only present once a
  // tenant has at least one past pay run to infer the calendar from — a
  // tenant can have multiple calendars (e.g. Fortnightly for employees vs
  // Monthly for contractors) and there's no way to know which is relevant
  // before that.
  const [tenantExpectedPeriod, setTenantExpectedPeriod] = useState<TenantExpectedPeriod | null>(null);
  const [superValidations, setSuperValidations] = useState<Record<string, EmployeeSuperValidation>>({});

  // Import workflow
  const [step, setStep] = useState<WorkflowStep>('upload');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [importRecord, setImportRecord] = useState<ImportRecord | null>(null);
  const [lines, setLines] = useState<ImportLine[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
  const selectionTouched = useRef(false);
  // Contractors normally sent to Xero, remembered across reports. null = still
  // loading for the current import.
  const [savedXeroSelection, setSavedXeroSelection] = useState<{ saved: boolean; lineIds: number[] } | null>(null);
  const [reportMonth, setReportMonth] = useState<string | null>(null);
  const [reportYear, setReportYear] = useState<string | null>(null);
  const [reportStartDate, setReportStartDate] = useState<string | null>(null);
  const [reportEndDate, setReportEndDate] = useState<string | null>(null);
  const [inferredCalendarType, setInferredCalendarType] = useState<'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY' | null>(null);
  const [filename, setFilename] = useState<string | undefined>();
  const [warnings, setWarnings] = useState<string[]>([]);

  // Pay run
  const [showConfirm, setShowConfirm] = useState(false);
  const [payrunSubmitting, setPayrunSubmitting] = useState(false);
  const [payRunResult, setPayRunResult] = useState<PayRunResult | null>(null);
  const [quickSuperFinalised, setQuickSuperFinalised] = useState(false);
  // QuickSuper portal members (from the imported Employees.csv) and the member
  // chosen/auto-matched for each report line.
  const [qsMembers, setQsMembers] = useState<QuickSuperMember[]>([]);
  const [qsMatches, setQsMatches] = useState<Record<string, QuickSuperMatch>>({});
  const [qsReloadKey, setQsReloadKey] = useState(0);
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  // Bumped after a pay run is created so the Past Runs view re-fetches when
  // the user navigates to it.
  const [pastRunsReloadKey, setPastRunsReloadKey] = useState(0);

  // Top-level view (sidebar nav). Lazy-init from URL+sessionStorage so a
  // /xero/callback return after the user clicked "Connect Xero" inside
  // Settings lands them straight back on Settings instead of Import Report.
  const [view, setView] = useState<AppView>(() => {
    if (typeof window === 'undefined') return 'main';
    if (
      window.location.pathname === '/xero/callback' &&
      window.sessionStorage.getItem(XERO_RETURN_TO_SETTINGS_KEY) === '1'
    ) {
      return 'settings';
    }
    return 'main';
  });

  // Errors
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [payrunError, setPayrunError] = useState<string | null>(null);
  const [payrunErrorIsConflict, setPayrunErrorIsConflict] = useState(false);
  const [payrunErrorXeroUrl, setPayrunErrorXeroUrl] = useState<string | null>(null);
  const [employeeLoadError, setEmployeeLoadError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [xeroOrgDisconnectedNotice, setXeroOrgDisconnectedNotice] = useState<string | null>(null);
  const [xeroConnectNotice, setXeroConnectNotice] = useState<string | null>(null);
  const [invoicepolishBaseUrl, setInvoicepolishBaseUrl] = useState<string | null>(null);
  const [payrollDiagnostic, setPayrollDiagnostic] = useState<string | null>(null);
  const [xeroPayrollUnauthorized, setXeroPayrollUnauthorized] = useState<XeroPayrollUnauthorizedDetail | null>(null);
  const superValidationRequestIds = React.useRef<Record<string, string>>({});
  const xeroCallbackExpectedSuccess = React.useRef(false);
  // Bumped on tenant change / disconnect / sign-out. In-flight Xero data
  // fetches (employees, calendars, super validations) capture this on entry
  // and bail out if the value has moved by the time the response arrives,
  // preventing stale rows from a disconnected tenant from repopulating UI.
  const xeroDataGeneration = React.useRef(0);

  // --- Init ---
  useEffect(() => {
    checkAuth();
    handleUrlParams();
    authApi.config()
      .then(({ data }) => setInvoicepolishBaseUrl(data.invoicepolishBaseUrl))
      .catch(() => setInvoicepolishBaseUrl(null));
  }, []);

  // Reset every piece of in-memory app state. Used both when the parent
  // InvoicePolish session disappears and on explicit logout, so no stale Xero
  // org / import / pay-run data is ever left visible behind the login modal.
  const clearLocalAuthState = useCallback(() => {
    // Invalidate any in-flight Xero / super-validation responses so they
    // can't race in after sign-out / 401 and repopulate state.
    xeroDataGeneration.current += 1;
    superValidationRequestIds.current = {};
    setUser(null);
    setShowLogin(true);
    setXeroConnected(false);
    setXeroTenants([]);
    setEmployees([]);
    setCalendars([]);
    setSuperValidations({});
    setStep('upload');
    setImportRecord(null);
    setLines([]);
    setSelectedLineIds(new Set());
    selectionTouched.current = false;
    autoMatchAttempted.current = new Set();
    setReportMonth(null);
    setReportYear(null);
    setReportStartDate(null);
    setReportEndDate(null);
    setInferredCalendarType(null);
    setFilename(undefined);
    setWarnings([]);
    setShowConfirm(false);
    setPayRunResult(null);
    setUploadError(null);
    setPayrunError(null);
    setEmployeeLoadError(null);
    setPayrollDiagnostic(null);
    setXeroPayrollUnauthorized(null);
    setXeroOrgDisconnectedNotice(null);
    setXeroConnectNotice(null);
  }, []);

  // --- Parent-session mirror poll ---
  // While the user is signed in, re-check InvoicePolish every 5 minutes via
  // our /auth/me endpoint (which forces a fresh upstream verify). On 401 the
  // backend has already destroyed contractors.sid; we just clear local state.
  useEffect(() => {
    if (!user) return;
    const POLL_MS = 5 * 60 * 1000;
    const id = window.setInterval(async () => {
      try {
        const { data } = await authApi.me();
        if (!data.authenticated) clearLocalAuthState();
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) clearLocalAuthState();
        // network/5xx — ignore, try again next tick
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [user, clearLocalAuthState]);

  // Centralised 401 handler — fired by the axios interceptor whenever any
  // authenticated API returns 401 (i.e. the backend just destroyed our
  // session because parent InvoicePolish auth is gone).
  useEffect(() => {
    const onUnauthorized = () => clearLocalAuthState();
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, [clearLocalAuthState]);

  // Xero payroll-scope-missing handler. The token InvoicePolish issued is
  // valid for accounting but not for payroll endpoints. Show a persistent
  // banner pointing the user back to InvoicePolish to reconnect Xero.
  useEffect(() => {
    const onPayrollUnauthorized = (e: Event) => {
      const detail = (e as CustomEvent<XeroPayrollUnauthorizedDetail>).detail;
      setXeroPayrollUnauthorized(detail);
    };
    window.addEventListener(XERO_PAYROLL_UNAUTHORIZED_EVENT, onPayrollUnauthorized);
    return () => window.removeEventListener(XERO_PAYROLL_UNAUTHORIZED_EVENT, onPayrollUnauthorized);
  }, []);

  function handleUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('auth_error');
    if (err) {
      setAuthError(err);
      console.warn('Auth error:', err);
    }
    // Xero connect return-to landing. InvoicePolish redirects the browser
    // here once the user finishes the Xero OAuth flow. Bootstrap
    // (checkAuth → checkXero) already re-fetches /xero/status, which carries
    // the updated hasPayrollAccess and the new tenant list. We just need to
    // strip the path so the URL bar doesn't keep showing /xero/callback. The
    // settings-return flag is consumed by the lazy view initializer above and
    // cleared here.
    const isXeroCallback = window.location.pathname === '/xero/callback';
    if (isXeroCallback) {
      window.sessionStorage.removeItem(XERO_RETURN_TO_SETTINGS_KEY);
      xeroCallbackExpectedSuccess.current = params.get('xero_connected') === 'true';

      const xeroError = params.get('xero_error');
      const selectOrgs = params.get('xero_select_orgs') === 'true';
      if (xeroError) {
        const contractorsEmail = params.get('contractors_email');
        const invoicePolishEmail = params.get('invoicepolish_email');
        const message =
          xeroError === 'session_user_mismatch'
            ? `You are signed into Contractors as ${contractorsEmail || 'this user'}, but InvoicePolish is signed in as ${invoicePolishEmail || 'a different user'}. Sign into InvoicePolish with the same account before connecting Xero.`
            : xeroError === 'email_not_verified'
            ? 'Verify your InvoicePolish email address before connecting Xero.'
            : xeroError === 'org_switch_locked'
            ? 'InvoicePolish blocked this organisation switch for the current billing period.'
            : 'Xero connection did not complete. Please try again.';
        setXeroConnectNotice(message);
        xeroCallbackExpectedSuccess.current = false;
      } else if (selectOrgs) {
        const max = params.get('max');
        setXeroConnectNotice(
          `Xero returned more organisations than your InvoicePolish plan can connect${max ? ` (${max} slot${max === '1' ? '' : 's'} available)` : ''}. Finish selecting organisations in InvoicePolish Settings.`
        );
        xeroCallbackExpectedSuccess.current = false;
      }
    }
    if (err || isXeroCallback) {
      window.history.replaceState({}, '', '/');
    }
  }

  async function startXeroConnect() {
    setXeroConnectNotice(null);
    window.sessionStorage.setItem(XERO_RETURN_TO_SETTINGS_KEY, '1');
    const { data } = await xeroApi.connectUrl();
    window.location.assign(data.url);
  }

  // Persist the org switch through the API (same path OrgSelector uses) so
  // it survives the next checkXero() refresh. Without the server-side
  // selectTenant call, checkXero would re-load /xero/status and snap the
  // active org back.
  async function handleSelectTenantFromSettings(tenantId: string) {
    const t = xeroTenants.find((x) => x.tenantId === tenantId);
    if (!t || t.isActive) return;
    try {
      await xeroApi.selectTenant(tenantId);
    } catch {
      // Persist failed — do NOT optimistically flip UI or wipe import state.
      // Pull authoritative truth from the server and bail.
      checkXero();
      return;
    }
    handleTenantChange(t);
  }

  // Disconnect a Xero organisation. Calls our backend, which round-trips
  // through InvoicePolish so all sibling apps see the change. After success
  // we re-pull /xero/status to refresh the Settings list and clear in-app
  // pay-run/import state if the active tenant just went away.
  async function handleDisconnectTenant(tenantId: string) {
    const wasActive = xeroTenants.find((t) => t.tenantId === tenantId)?.isActive === true;
    await xeroApi.disconnect(tenantId);
    if (wasActive) {
      // Active org just disappeared — bump the generation so any in-flight
      // employees / calendars / super-validation responses are discarded,
      // then wipe local UI state so nothing points at a dead tenant.
      xeroDataGeneration.current += 1;
      setEmployees([]);
      setCalendars([]);
      setEmployeeLoadError(null);
      handleStartNew();
    }
    await checkXero();
  }

  async function handleUpdateName(name: string) {
    await accountApi.updateName(name);
    setUser((prev) => (prev ? { ...prev, name } : prev));
  }

  async function handleSignOut() {
    let logoutUrl: string | null = null;
    try {
      const { data } = await authApi.logout();
      logoutUrl = data.logoutUrl ?? null;
    } catch {
      // Even if the server call fails, drop local state so the user isn't
      // stuck staring at signed-in UI. A successful call redirects through
      // InvoicePolish so its master session is cleared too.
    } finally {
      setView('main');
      clearLocalAuthState();
      window.sessionStorage.removeItem(XERO_RETURN_TO_SETTINGS_KEY);
      if (logoutUrl) {
        window.location.assign(logoutUrl);
      }
    }
  }

  async function checkAuth() {
    try {
      const { data } = await authApi.me();
      if (data.authenticated && data.user) {
        setUser(data.user);
        await checkXero();
      } else {
        clearLocalAuthState();
      }
    } catch {
      clearLocalAuthState();
    } finally {
      setAuthLoading(false);
    }
  }

  const checkXero = useCallback(async () => {
    try {
      const { data } = await xeroApi.status();
      setXeroOrgDisconnectedNotice(null);
      setXeroConnected(data.connected);
      const tenants = data.tenants ?? [];
      setXeroTenants(tenants);
      if (xeroCallbackExpectedSuccess.current) {
        xeroCallbackExpectedSuccess.current = false;
        if (!data.connected || tenants.length === 0) {
          setXeroConnectNotice(
            'Xero reported a successful authorisation, but no persisted InvoicePolish Xero connection was found for this Contractors user. Please confirm you are signed into the same InvoicePolish account and try again.'
          );
          console.warn('[xero-callback] success reported but no persisted tenants returned');
        }
      }
      // Find the tenant we're about to use (active, or first if none flagged).
      const activeTenant =
        tenants.find((t) => t.tenantId === data.activeTenantId) ??
        tenants.find((t) => t.isActive) ??
        tenants[0];
      if (data.connected && activeTenant && activeTenant.hasPayrollAccess === false) {
        // Proactively surface the reconnect banner and skip every payroll
        // call until the user reconnects in InvoicePolish.
        setXeroPayrollUnauthorized({
          message:
            'Xero is connected, but payroll access has not been authorised. Please reconnect Xero in InvoicePolish Settings, then return here and retry.',
          reconnectUrl: null,
        });
        return;
      }
      // Payroll access OK — clear any stale banner from a previous tenant.
      setXeroPayrollUnauthorized(null);
      if (data.connected) {
        loadXeroData();
      }
    } catch (err: unknown) {
      // 401 is handled centrally by the axios interceptor → clearLocalAuthState.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) return;
      setXeroConnected(false);
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to load Xero connections');
      setEmployeeLoadError(msg);
    }
  }, []);

  // Xero org disconnected handler. Fired when any API call returns HTTP 409
  // with code `xero_org_disconnected` — meaning InvoicePolish no longer has
  // this tenant in its connection list. Immediately reconcile local state so
  // the UI doesn't keep referencing a dead tenantId.
  useEffect(() => {
    const onOrgDisconnected = (e: Event) => {
      const detail = (e as CustomEvent<XeroOrgDisconnectedDetail>).detail;
      // Bump generation to cancel any in-flight Xero data responses so stale
      // employees/calendars from the disconnected tenant can't race back in.
      xeroDataGeneration.current += 1;
      superValidationRequestIds.current = {};
      setEmployees([]);
      setCalendars([]);
      setSuperValidations({});
      setEmployeeLoadError(null);
      handleStartNew();
      setXeroOrgDisconnectedNotice(
        detail.message ?? 'This Xero organisation has been disconnected.'
      );
      // Re-fetch /xero/status — the server reconciles the active tenant
      // against the upstream connection list, so this also updates the nav.
      checkXero();
    };
    window.addEventListener(XERO_ORG_DISCONNECTED_EVENT, onOrgDisconnected);
    return () => window.removeEventListener(XERO_ORG_DISCONNECTED_EVENT, onOrgDisconnected);
  }, [checkXero]);

  async function loadXeroData() {
    // Guard every Payroll AU fetch path (initial load, "Retry", "Refresh
    // employees") with the same hasPayrollAccess check. Without this guard
    // the user can re-trigger payroll calls by clicking Retry while the
    // banner is up.
    setXeroTenants((currentTenants) => {
      const active =
        currentTenants.find((t) => t.isActive) ?? currentTenants[0];
      if (active && active.hasPayrollAccess === false) {
        setXeroPayrollUnauthorized({
          message:
            'Xero is connected, but payroll access has not been authorised. Please reconnect Xero in InvoicePolish Settings, then return here and retry.',
          reconnectUrl: null,
        });
      }
      return currentTenants;
    });
    // If the banner just got set (or was already set), skip the fetch.
    const activeTenant =
      xeroTenants.find((t) => t.isActive) ?? xeroTenants[0];
    if (activeTenant && activeTenant.hasPayrollAccess === false) return;
    setEmployeeLoadError(null);
    const generation = xeroDataGeneration.current;
    try {
      const [empRes, calRes] = await Promise.all([
        xeroApi.employees(),
        xeroApi.payrollCalendars(),
      ]);
      // Tenant changed / disconnected mid-flight — drop stale data.
      if (xeroDataGeneration.current !== generation) return;
      setEmployees(empRes.data.employees);
      setCalendars(calRes.data.calendars);
    } catch (err: unknown) {
      if (xeroDataGeneration.current !== generation) return;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) return; // handled centrally
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to load payroll data');
      console.warn('Failed to load Xero payroll data:', msg);
      setEmployeeLoadError(msg);
    }
  }

  function newRequestId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  async function validateEmployeeSuper(employeeIds: string[], forceRefresh = false) {
    const uniqueIds = Array.from(new Set(employeeIds.filter(Boolean)));
    if (uniqueIds.length === 0) return;

    const requestIds = Object.fromEntries(uniqueIds.map((employeeId) => [employeeId, newRequestId()]));
    setSuperValidations((prev) => {
      const next = { ...prev };
      for (const employeeId of uniqueIds) {
        const existing = prev[employeeId];
        superValidationRequestIds.current[employeeId] = requestIds[employeeId];
        next[employeeId] = {
          ...existing,
          employeeId,
          superStatus: 'CHECKING',
          message: 'Checking super fund...',
          requestId: requestIds[employeeId],
        };
      }
      return next;
    });

    try {
      const { data } = await Promise.race([
        xeroApi.validateSelectedEmployees(uniqueIds, forceRefresh),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error('Super check timed out. Retry.')), 30_000);
        }),
      ]);
      setSuperValidations((prev) => {
        const next = { ...prev };
        for (const validation of data.employees) {
          const requestId = requestIds[validation.employeeId];
          if (superValidationRequestIds.current[validation.employeeId] !== requestId) continue;
          next[validation.employeeId] = { ...validation, requestId };
        }
        return next;
      });
    } catch (err: unknown) {
      setSuperValidations((prev) => {
        const next = { ...prev };
        for (const employeeId of uniqueIds) {
          const requestId = requestIds[employeeId];
          if (superValidationRequestIds.current[employeeId] !== requestId) continue;
          next[employeeId] = {
            employeeId,
            superStatus: 'ERROR',
            message: 'Could not verify super fund. Retry.',
            requestId,
          };
        }
        return next;
      });
      console.warn('[SuperCheck] error', err instanceof Error ? err.message : String(err));
    }
  }

  function handleTenantChange(selected: XeroTenant) {
    // Invalidate any in-flight Xero fetches for the previous tenant so they
    // can't repopulate the UI with the wrong org's employees/calendars.
    xeroDataGeneration.current += 1;
    setXeroTenants((prev) => prev.map((t) => ({ ...t, isActive: t.tenantId === selected.tenantId })));
    setEmployees([]);
    setCalendars([]);
    setEmployeeLoadError(null);
    handleStartNew();
    // Re-evaluate payroll access for the newly selected tenant before
    // attempting any Payroll AU call. If the new tenant lacks payroll
    // access, checkXero shows the banner and skips loadXeroData.
    checkXero();
  }

  const activeTenantId = xeroTenants.find((t) => t.isActive)?.tenantId ?? null;

  // Fetch the estimated next pending period whenever the active tenant
  // changes. This is a UX heads-up only — the authoritative check still
  // happens server-side (via a fresh GET) when the pay run is actually
  // created, so a wrong estimate here can't cause bad data to reach Xero.
  useEffect(() => {
    if (!xeroConnected || !activeTenantId) {
      setTenantExpectedPeriod(null);
      return;
    }
    let cancelled = false;
    payrunApi.expectedPeriodForTenant()
      .then(({ data }) => { if (!cancelled) setTenantExpectedPeriod(data); })
      .catch(() => { if (!cancelled) setTenantExpectedPeriod(null); });
    return () => { cancelled = true; };
  }, [xeroConnected, activeTenantId]);

  // --- Automated Xero report result ---
  // Receives the same UploadResult shape the upload route returns, so the
  // existing review / pay-run flow runs identically for both workflows.
  function handleCalculateResult(data: UploadResult) {
    setImportRecord(data.import);
    setLines(data.lines);
    setReportMonth(data.reportMonth);
    setReportYear(data.reportYear);
    setReportStartDate(data.reportStartDate ?? null);
    setReportEndDate(data.reportEndDate ?? null);
    setInferredCalendarType(data.inferredCalendarType ?? null);
    setFilename(data.import.filename);
    setWarnings(data.warnings || []);
    setUploadError(null);
    setStep('review');
  }

  // --- Upload ---
  async function handleFile(file: File) {
    if (!user) { setShowLogin(true); return; }
    if (!xeroConnected) {
      setUploadError('Connect a Xero organisation in InvoicePolish before uploading a report.');
      return;
    }

    const previousStep = step;
    const previousFilename = filename;
    setUploadError(null);
    setUploadNotice(null);
    setUploadLoading(true);
    setFilename(file.name);
    setStep('upload');

    try {
      const { data } = await importApi.upload(file);
      if ('kind' in data) {
        // QuickSuper Employees.csv: refreshes the member list, not a new report.
        setQsReloadKey((n) => n + 1);
        setUploadNotice(
          `QuickSuper employee list updated: ${data.total} member${data.total === 1 ? '' : 's'} (${data.created} new, ${data.updated} updated).`
        );
        setFilename(previousFilename);
        setStep(previousStep);
        return;
      }
      setImportRecord(data.import);
      setLines(data.lines);
      setReportMonth(data.reportMonth);
      setReportYear(data.reportYear);
      setReportStartDate(data.reportStartDate ?? null);
      setReportEndDate(data.reportEndDate ?? null);
      setInferredCalendarType(data.inferredCalendarType ?? null);
      setWarnings(data.warnings || []);
      setStep('review');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to parse file';
      setUploadError(msg);
      setFilename(previousFilename);
      setStep(previousStep);
    } finally {
      setUploadLoading(false);
    }
  }

  // --- QuickSuper members + remembered matches for the current import ---
  useEffect(() => {
    if (!importRecord) { setQsMatches({}); return; }
    let cancelled = false;
    quickSuperApi.getState(importRecord.id)
      .then(({ data }) => {
        if (cancelled) return;
        setQsMembers(data.members);
        setQsMatches(data.matches);
      })
      .catch(() => { /* optional feature: the table still works without it */ });
    return () => { cancelled = true; };
  }, [importRecord?.id, qsReloadKey]);

  async function handleQuickSuperChange(line: ImportLine, memberId: number | null) {
    if (!importRecord) return;
    setQsMatches((prev) => ({ ...prev, [line.id]: { memberId, source: memberId ? 'saved' : null } }));
    try {
      const { data } = await quickSuperApi.setMapping(importRecord.id, line.id, memberId);
      setQsMatches((prev) => ({ ...prev, [line.id]: data.match }));
    } catch {
      setQsReloadKey((n) => n + 1); // resync with the server
    }
  }

  // --- Auto-match contractors to Xero employees by name ---
  // Track which lines we've already attempted to auto-map. Without this, the
  // effect would re-fire on every setLines() (because each PATCH triggers a
  // state update before the in-flight ones finish), causing duplicate PATCHes
  // for the same line.
  const autoMatchAttempted = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (step !== 'review' || employees.length === 0 || lines.length === 0 || !importRecord) return;
    const unmapped = lines.filter((l) => !l.xeroEmployeeId && !autoMatchAttempted.current.has(l.id));
    if (unmapped.length === 0) return;

    unmapped.forEach(async (line) => {
      autoMatchAttempted.current.add(line.id);
      const match = autoMatchEmployee(line.contractorName, employees, inferredCalendarType);
      if (!match) return;
      try {
        const { data } = await importApi.updateLine(importRecord.id, line.id, {
          xeroEmployeeId: match.EmployeeID,
          xeroEmployeeName: `${match.FirstName} ${match.LastName}`,
        });
        setLines((prev) => prev.map((l) => (l.id === line.id ? data.line : l)));
      } catch { /* silent — user can map manually */ }
    });
  // Include `lines` and `importRecord` so this fires after upload when
  // employees were already loaded. The attempted-set prevents loops.
  }, [employees, inferredCalendarType, step, lines, importRecord]);

  useEffect(() => {
    if (step !== 'review' || lines.length === 0) return;
    const idsToValidate = Array.from(new Set(
      lines
        .map((line) => line.xeroEmployeeId)
        .filter((id): id is string => !!id)
        .filter((id) => {
          const status = superValidations[id]?.superStatus ?? 'UNKNOWN';
          return status === 'UNKNOWN';
        })
    ));
    if (idsToValidate.length > 0) {
      validateEmployeeSuper(idsToValidate, false);
    }
  }, [lines, step]);

  // --- Pay run ---
  const selectedLines = lines.filter((line) => selectedLineIds.has(line.id));
  const selectedSuperReady =
    selectedLines.length > 0 &&
    selectedLines.every((line) => {
      if (!line.xeroEmployeeId) return false;
      return superValidations[line.xeroEmployeeId]?.superStatus === 'VALID';
    });
  const resolvedCalendarId = resolvePayrollCalendarId(selectedLines, employees, calendars, inferredCalendarType);
  const resolvedCalendarType =
    calendars.find((calendar) => calendar.PayrollCalendarID === resolvedCalendarId)?.CalendarType?.toUpperCase() ??
    inferredCalendarType;
  const reportFrequencyType = inferredCalendarType ?? resolvedCalendarType;
  const withinXeroLimit = selectedLines.length <= MAX_XERO_SELECTION;
  const allReady = withinXeroLimit && selectedSuperReady && selectedLines.every((line) => {
    const employee = employees.find((item) => item.EmployeeID === line.xeroEmployeeId);
    const employeeFrequency = employeePayrollCalendarType(employee);
    return !reportFrequencyType || !employeeFrequency || employeeFrequency === reportFrequencyType;
  });

  useEffect(() => {
    if (!importRecord) { setSavedXeroSelection(null); return; }
    let cancelled = false;
    setSavedXeroSelection(null);
    importApi.getXeroSelection(importRecord.id)
      .then(({ data }) => { if (!cancelled) setSavedXeroSelection(data); })
      .catch(() => { if (!cancelled) setSavedXeroSelection({ saved: false, lineIds: [] }); });
    return () => { cancelled = true; };
  }, [importRecord?.id]);

  // Remember 1-5 ticked contractors as the default for future reports.
  function rememberXeroSelection(ids: Set<number>) {
    if (!importRecord || ids.size < 1 || ids.size > MAX_XERO_SELECTION) return;
    importApi.saveXeroSelection(importRecord.id, Array.from(ids)).catch(() => { /* best effort */ });
  }

  // Default ticks. Five or fewer contractors: all ticked. Otherwise, if a Send-to-Xero set has been remembered, tick exactly those
  // contractors that appear in this report (never substitute others). Otherwise,
  // once Xero's employee detail checks return, preselect the first five rows that
  // are genuinely ready, recomputed while checks arrive so row order rather than
  // response timing decides. Once the user changes a checkbox, it's left alone.
  useEffect(() => {
    if (selectionTouched.current || step !== 'review') return;
    if (importRecord && savedXeroSelection === null) return; // still loading
    // Xero takes up to five contractors, so a report that fits is ticked in full.
    if (lines.length > 0 && lines.length <= MAX_XERO_SELECTION) {
      setSelectedLineIds(new Set(lines.map((line) => line.id)));
      return;
    }
    if (savedXeroSelection?.saved) {
      setSelectedLineIds(new Set(savedXeroSelection.lineIds));
      return;
    }
    const readyIds = lines
      .filter((line) => {
        if (!line.xeroEmployeeId) return false;
        if (superValidations[line.xeroEmployeeId]?.superStatus !== 'VALID') return false;
        const employee = employees.find((item) => item.EmployeeID === line.xeroEmployeeId);
        const employeeFrequency = employeePayrollCalendarType(employee);
        return !reportFrequencyType || !employeeFrequency || employeeFrequency === reportFrequencyType;
      })
      .slice(0, DEFAULT_XERO_SELECTION_COUNT)
      .map((line) => line.id);
    setSelectedLineIds(new Set(readyIds));
  }, [employees, lines, reportFrequencyType, step, superValidations, savedXeroSelection, importRecord]);

  function handleCreatePayRun() {
    setPayrunError(null);
    setPayrunErrorIsConflict(false);
    setPayrunErrorXeroUrl(null);
    setShowConfirm(true);
  }

  async function handleConfirmPayRun(params: {
    payrollCalendarId: string;
    payPeriodStartDate: string;
    payPeriodEndDate: string;
    paymentDate: string;
  }) {
    if (!importRecord) return;
    setPayrunSubmitting(true);
    setPayrunError(null);

    try {
      const { data } = await payrunApi.create({
        importId: importRecord.id,
        selectedLineIds: selectedLines.map((line) => line.id),
        ...params,
      });
      setPayRunResult(data);
      rememberXeroSelection(new Set(selectedLines.map((line) => line.id)));
      setQuickSuperFinalised(false);
      setShowConfirm(false);
      setStep('success');
      setPastRunsReloadKey((n) => n + 1);
    } catch (err: unknown) {
      const respData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      let msg: string;
      if (respData?.code === 'xero_period_mismatch') {
        // Xero assigned a different period (e.g. April) than the one requested (e.g. May).
        // Xero enforces sequential processing — the earlier period must go first.
        msg = typeof respData.error === 'string'
          ? respData.error
          : 'Xero assigned the pay run to an unexpected period. Xero processes pay periods in order — please check Xero Payroll and try again.';
        setPayrunErrorIsConflict(true);
        // Server includes a Xero URL for the wrong-period draft it created.
        setPayrunErrorXeroUrl(typeof respData.xeroUrl === 'string' ? respData.xeroUrl : null);
      } else if (respData?.code === 'xero_draft_pay_run_conflict') {
        // A draft for a different period is blocking creation — use the
        // server-formatted message which includes the blocking period (e.g. "April 2026").
        msg = typeof respData.message === 'string'
          ? respData.message
          : 'Xero already has a draft pay run waiting to be processed. Please open Xero Payroll, review/post or delete the existing draft pay run, then return here and try again.';
        setPayrunErrorIsConflict(true);
      } else {
        msg = typeof respData?.error === 'string' ? respData.error : 'Failed to create pay run';
        setPayrunErrorIsConflict(false);
      }
      setPayrunError(msg);
      setShowConfirm(false);
    } finally {
      setPayrunSubmitting(false);
    }
  }

  function handleStartNew() {
    setStep('upload');
    setImportRecord(null);
    setLines([]);
    setSelectedLineIds(new Set());
    selectionTouched.current = false;
    autoMatchAttempted.current = new Set();
    setReportMonth(null);
    setReportYear(null);
    setReportStartDate(null);
    setReportEndDate(null);
    setInferredCalendarType(null);
    setFilename(undefined);
    setWarnings([]);
    setPayRunResult(null);
    setUploadError(null);
    setPayrunError(null);
    setPayrunErrorIsConflict(false);
    setPayrunErrorXeroUrl(null);
    setSuperValidations({});
    // Drop any in-flight super validations so a late response can't
    // repopulate a row after the user has cleared / switched tenant.
    superValidationRequestIds.current = {};
  }

  // --- Render ---
  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <svg className="h-8 w-8 animate-spin text-slate-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <>
      {showLogin && <LoginModal />}

      {/* Pay run conflict / period-mismatch modal — shown when Xero rejects the
          creation with a actionable reason (existing draft, wrong period, etc.) */}
      {payrunError && payrunErrorIsConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <svg className="h-5 w-5 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Pay Run Not Created</h2>
                  <p className="text-sm text-gray-500">Action required in Xero</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">{payrunError}</p>
              {payrunErrorXeroUrl && (
                <a
                  href={payrunErrorXeroUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  Open Xero Pay Runs
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                </a>
              )}
            </div>

            <div className="border-t border-gray-100 px-6 py-4">
              <button
                onClick={() => { setPayrunError(null); setPayrunErrorIsConflict(false); setPayrunErrorXeroUrl(null); }}
                className="btn-primary w-full"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirm && importRecord && (
        <ConfirmModal
          lines={selectedLines}
          reportMonth={reportMonth}
          reportYear={reportYear}
          reportStartDate={reportStartDate}
          reportEndDate={reportEndDate}
          inferredCalendarType={inferredCalendarType}
          calendars={calendars}
          employees={employees}
          onConfirm={handleConfirmPayRun}
          onCancel={() => setShowConfirm(false)}
          isSubmitting={payrunSubmitting}
        />
      )}

      <Layout
        user={user}
        xeroConnected={xeroConnected}
        xeroTenants={xeroTenants}
        view={view}
        onNavigate={setView}
        onLogin={() => setShowLogin(true)}
        onSignOut={handleSignOut}
      >
        {!user ? (
          // Signed-out state: render nothing behind the login modal so no
          // stale Xero / import / pay-run content is ever visible.
          <div className="flex-1" />
        ) : view === 'admin' && user.isAdmin ? (
          <AdminPage />
        ) : view === 'past-runs' ? (
          <PastRuns reloadKey={pastRunsReloadKey} xeroTenants={xeroTenants} />
        ) : view === 'settings' ? (
          <Settings
            user={user}
            xeroTenants={xeroTenants}
            xeroLoading={false}
            xeroNotice={xeroConnectNotice}
            onSelectTenant={handleSelectTenantFromSettings}
            onDisconnectTenant={handleDisconnectTenant}
            onConnectXeroIntent={startXeroConnect}
            onRefreshXero={checkXero}
            onSignOut={handleSignOut}
            onUpdateName={handleUpdateName}
          />
        ) : step === 'success' && payRunResult ? (
          <SuccessState result={payRunResult} onStartNew={handleStartNew} onNext={() => setStep('quicksuper')} />
        ) : step === 'quicksuper_preview' ? (
          <QuickSuperState
            importId={importRecord?.id}
            members={qsMembers}
            matches={qsMatches}
            onChoose={(lineId, memberId) => {
              const line = lines.find((l) => l.id === lineId);
              if (line) handleQuickSuperChange(line, memberId);
            }}
            periodStart={(reportStartDate ?? payRunResult?.payPeriodStartDate ?? '').slice(0, 10)}
            periodEnd={(reportEndDate ?? payRunResult?.payPeriodEndDate ?? '').slice(0, 10)}
            preview={{
              lines: lines
                .filter((line) => !selectedLineIds.has(line.id))
                .map((line) => ({
                  id: line.id,
                  contractorName: line.contractorName,
                  debitTotal: line.debitTotal,
                  superRate: line.superRate,
                  superAmount: line.superAmount,
                })),
              xeroTotal: selectedLines.reduce((sum, line) => sum + parseFloat(line.superAmount), 0),
            }}
            onBack={() => setStep('review')}
          />
        ) : step === 'quicksuper' && payRunResult ? (
          <QuickSuperState
            importId={importRecord?.id}
            members={qsMembers}
            matches={qsMatches}
            onChoose={(lineId, memberId) => {
              const line = lines.find((l) => l.id === lineId);
              if (line) handleQuickSuperChange(line, memberId);
            }}
            periodStart={(reportStartDate ?? payRunResult?.payPeriodStartDate ?? '').slice(0, 10)}
            periodEnd={(reportEndDate ?? payRunResult?.payPeriodEndDate ?? '').slice(0, 10)}
            result={payRunResult}
            onBack={quickSuperFinalised ? handleStartNew : () => setStep('success')}
            onFinalised={() => { setQuickSuperFinalised(true); setPastRunsReloadKey((n) => n + 1); }}
          />
        ) : (
          <div className="flex-1 px-6 py-8 lg:px-10 max-w-6xl mx-auto w-full">
            <div className="mb-8 flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contractor super</p>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Import Contractor Report</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Upload your Xero contractor labour report to calculate super and prepare a pay run.
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-slate-400" />
                {xeroConnected ? 'Xero connected' : 'No Xero org connected'}
              </div>
            </div>

            {authError && (
              <div className="mb-4 rounded-lg border border-slate-300 bg-white px-4 py-3">
                <p className="text-sm text-slate-700">Sign-in failed: {authError}. Try again.</p>
              </div>
            )}

            {xeroConnectNotice && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4">
                <div className="flex items-start gap-4">
                  <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Xero connection not completed</p>
                    <p className="mt-1 text-xs text-amber-800">{xeroConnectNotice}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setXeroConnectNotice(null)}
                    aria-label="Dismiss"
                    className="flex-shrink-0 text-amber-600 hover:text-amber-900 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {xeroOrgDisconnectedNotice && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4">
                <div className="flex items-start gap-4">
                  <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Xero organisation disconnected</p>
                    <p className="mt-1 text-xs text-amber-800">{xeroOrgDisconnectedNotice}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setXeroOrgDisconnectedNotice(null)}
                    aria-label="Dismiss"
                    className="flex-shrink-0 text-amber-600 hover:text-amber-900 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {xeroPayrollUnauthorized && (
                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4">
                  <div className="flex items-start gap-4">
                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-900">
                        Xero is connected, but payroll access has not been authorised
                      </p>
                      <p className="mt-1 text-xs text-amber-800">
                        Please reconnect Xero to enable payroll features. You'll be sent to
                        InvoicePolish to complete the Xero authorisation, then returned here.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        startXeroConnect().catch((err) => {
                          const msg = err instanceof Error ? err.message : 'Could not start Xero connection.';
                          setXeroConnectNotice(msg);
                        });
                      }}
                      className="btn-primary text-sm py-2 flex-shrink-0"
                    >
                      Connect Xero
                    </button>
                  </div>
                </div>
            )}

            {/* Org selector + global connection errors — above both workflow panels */}
            <div className="space-y-4">
              {xeroConnected && xeroTenants.length > 0 && (
                <OrgSelector
                  tenants={xeroTenants}
                  onTenantChange={handleTenantChange}
                />
              )}

              {xeroConnected && tenantExpectedPeriod?.payrollCalendarId && tenantExpectedPeriod.payPeriodStartDate && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
                  {tenantExpectedPeriod.source === 'blocking-draft' ? (
                    <>
                      <span className="font-semibold text-slate-700">Unposted draft pending in Xero: </span>
                      {formatExpectedPeriod(tenantExpectedPeriod)}
                      {tenantExpectedPeriod.payrollCalendarName ? ` (${tenantExpectedPeriod.payrollCalendarName})` : ''}
                      {' — post or delete it in Xero before creating a new pay run for this calendar.'}
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-slate-700">Expected Xero pay period: </span>
                      {formatExpectedPeriod(tenantExpectedPeriod)}
                      {tenantExpectedPeriod.payrollCalendarName ? ` (${tenantExpectedPeriod.payrollCalendarName})` : ''}
                    </>
                  )}
                </div>
              )}

              {xeroConnected && employeeLoadError && (
                <div className="rounded-lg border border-slate-300 bg-white px-5 py-4 space-y-3">
                  <div className="flex items-start gap-4">
                    <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">Failed to load payroll data</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => loadXeroData()} className="btn-primary text-sm py-2">
                        Retry
                      </button>
                    </div>
                  </div>
                  <div className="relative rounded-md bg-slate-50 border border-slate-200 p-3">
                    <pre className="text-xs text-slate-600 whitespace-pre-wrap break-all font-mono leading-relaxed">
                      {payrollDiagnostic ? payrollDiagnostic : `Error: ${employeeLoadError}`}
                    </pre>
                    <button
                      onClick={() => navigator.clipboard.writeText(payrollDiagnostic ?? `Error: ${employeeLoadError}`)}
                      className="absolute top-2 right-2 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-500 hover:text-slate-800 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                  <button
                    onClick={async () => {
                      setPayrollDiagnostic('Running diagnostic...');
                      try {
                        const { data } = await xeroApi.testPayroll();
                        setPayrollDiagnostic(JSON.stringify(data, null, 2));
                      } catch (err: unknown) {
                        const msg = (err as { response?: { data?: unknown } })?.response?.data;
                        setPayrollDiagnostic(JSON.stringify(msg ?? String(err), null, 2));
                      }
                    }}
                    className="text-xs text-slate-500 underline"
                  >
                    Run full diagnostic (Settings + Employees test)
                  </button>
                </div>
              )}

              {/* No Xero orgs connected via InvoicePolish */}
              {user && !xeroConnected && (
                  <div className="flex items-start gap-4 rounded-lg border border-slate-300 bg-white px-5 py-4">
                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">No Xero organisation connected</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Connect a Xero organisation through InvoicePolish to start importing
                        contractor reports. You'll be sent to InvoicePolish to authorise Xero,
                        then returned here.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        startXeroConnect().catch((err) => {
                          const msg = err instanceof Error ? err.message : 'Could not start Xero connection.';
                          setXeroConnectNotice(msg);
                        });
                      }}
                      className="btn-primary text-sm py-2 flex-shrink-0"
                    >
                      Connect Xero
                    </button>
                  </div>
              )}
            </div>

            {/* Two workflow panels — equal height side by side */}
            <div className="mt-4 grid gap-4 lg:grid-cols-2 lg:items-stretch">
              {/* LEFT — existing XLS drag-and-drop workflow */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Import XLS Report
                </p>
                <div className="flex-1">
                  <UploadZone
                    onFile={handleFile}
                    isLoading={uploadLoading}
                    filename={step !== 'upload' ? filename : undefined}
                  />
                </div>
              </div>

              {/* RIGHT — automated Xero report workflow */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Automated Xero Report
                </p>
                <div className="flex-1">
                  <AutomatedXeroPanel
                    activeTenantId={activeTenantId}
                    xeroConnected={xeroConnected}
                    onCalculateResult={handleCalculateResult}
                    expectedPeriod={tenantExpectedPeriod}
                  />
                </div>
              </div>
            </div>

            {uploadNotice && (
              <div className="mt-3 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500" />
                <p className="text-sm text-emerald-800">{uploadNotice}</p>
              </div>
            )}

            {uploadError && (
              <div className="mt-4 flex items-start gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3">
                <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />
                <p className="text-sm text-slate-700">{uploadError}</p>
              </div>
            )}

            {warnings.length > 0 && step === 'review' && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                {warnings.map((w, i) => (
                  <p key={i} className="text-xs text-slate-500">{w}</p>
                ))}
              </div>
            )}

            {step === 'review' && lines.length > 0 && importRecord && (
              <div className="mt-8">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight text-slate-950">
                      Parsed Results
                      {reportMonth && reportYear && (
                        <span className="ml-2 text-sm font-normal text-slate-400">
                          {reportMonth} {reportYear}
                        </span>
                      )}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Review contractor DEBIT totals, adjust super rates, and map each contractor to a Xero employee.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                      <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1">
                        Start: <span className="font-semibold text-slate-800">{fmtReportDate(reportStartDate)}</span>
                      </span>
                      <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1">
                        End: <span className="font-semibold text-slate-800">{fmtReportDate(reportEndDate)}</span>
                      </span>
                      <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1">
                        Pay frequency: <span className="font-semibold text-slate-800">{calendarTypeLabel(reportFrequencyType)}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 self-start sm:self-auto">
                    <button
                      onClick={handleStartNew}
                      className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
                    >
                      Upload different file
                    </button>
                    <button
                      onClick={handleStartNew}
                      className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
                    >
                      Start Over
                    </button>
                  </div>
                </div>

                <ResultsTable
                  importId={importRecord.id}
                  lines={lines}
                  employees={employees}
                  superValidations={superValidations}
                  inferredCalendarType={inferredCalendarType}
                  resolvedCalendarType={reportFrequencyType}
                  calendars={calendars}
                  onLinesChange={setLines}
                  selectedLineIds={selectedLineIds}
                  onSelectionChange={(lineId, selected) => {
                    selectionTouched.current = true;
                    const next = new Set(selectedLineIds);
                    if (selected) next.add(lineId);
                    if (!selected) next.delete(lineId);
                    setSelectedLineIds(next);
                    rememberXeroSelection(next);
                  }}
                  quickSuperMembers={qsMembers}
                  quickSuperMatches={qsMatches}
                  onQuickSuperChange={handleQuickSuperChange}
                  onValidateEmployees={validateEmployeeSuper}
                  onRefreshEmployees={async () => {
                    await loadXeroData();
                    const mappedIds = lines
                      .map((line) => line.xeroEmployeeId)
                      .filter((id): id is string => !!id);
                    await validateEmployeeSuper(mappedIds, true);
                  }}
                />

                <SummaryPanel
                  lines={selectedLines}
                  employees={employees}
                  superValidations={superValidations}
                  reportMonth={reportMonth}
                  reportYear={reportYear}
                  onCreatePayRun={handleCreatePayRun}
                  onShowQuickSuper={() => setStep('quicksuper_preview')}
                  canCreate={allReady}
                  selectionLimit={MAX_XERO_SELECTION}
                />

                {payrunError && !payrunErrorIsConflict && (
                  <div className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="text-sm text-slate-700">{payrunError}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Layout>
    </>
  );
}
